import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Users2, HeartHandshake, Sparkles } from "lucide-react";
import { INITIATIVES, DIRECTORS, FOUNDATION_INFO, EVENTS } from "@/lib/initiatives";
import ImpactCounter from "@/components/ImpactCounter";

export default function Home() {
  return (
    <>
      {/* ========== HERO ========== */}
      <section data-testid="hero-section" className="relative overflow-hidden section-py">
        <div className="mx-auto max-w-7xl container-x grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#0A2540]/70 border border-[#0A2540]/15 rounded-full px-4 py-1.5">
              <Sparkles className="w-3.5 h-3.5" /> For Growth. For Harmony. For All.
            </span>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0A2540]">
              Help us build a{" "}
              <span className="italic text-[#D4AF37]">healthier, educated</span>{" "}
              & prosperous India.
            </h1>
            <p className="mt-6 text-lg text-[#4A5568] max-w-xl">
              UB Maitreya Foundation invests in mental health, education, women's
              empowerment, natural farming, MSMEs and civic sense — the seven pillars
              of a proud, thriving India.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/donate"
                data-testid="hero-donate-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] text-white px-8 py-3.5 font-medium hover:bg-[#D4AF37] hover:text-[#0A2540] transition-colors"
              >
                Donate & Become a UB Mitra <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/csr"
                data-testid="hero-csr-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#0A2540] text-[#0A2540] px-8 py-3.5 font-medium hover:bg-[#0A2540] hover:text-white transition-colors"
              >
                Partner with us (CSR)
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[
                { icon: Users2, label: "Transforming Lives" },
                { icon: HeartHandshake, label: "Empowering Communities" },
                { icon: Award, label: "Strengthening India" },
                { icon: ShieldCheck, label: "80G Certified" },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-3">
                  <s.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                  <div className="text-xs uppercase tracking-wider text-[#0A2540]/80">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 fade-up delay-2 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-[#0A2540]/10">
              <img
                src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80"
                alt="Communities we serve"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 grain" />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                <div className="text-white/90 text-xs uppercase tracking-[0.18em]">
                  Trust in Action
                </div>
                <div className="font-serif-display text-white text-2xl mt-1">
                  Every contribution changes a life.
                </div>
              </div>
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 rounded-2xl bg-[#0A2540] text-white p-5 w-64 shadow-lg">
              <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37]">80G Approved</div>
              <div className="font-serif-display text-lg mt-1">Section 12AB + 80G</div>
              <div className="text-xs text-white/70 mt-1">
                Approval No. {FOUNDATION_INFO.reg80g}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== INITIATIVES — brochure-style focus areas ========== */}
      <section data-testid="initiatives-section" className="section-py bg-white border-y border-[#0A2540]/10">
        <div className="mx-auto max-w-7xl container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">We Work For</div>
              <h2 className="mt-3 font-serif-display text-3xl md:text-5xl text-[#0A2540] max-w-2xl">
                Our seven focus areas.
              </h2>
              <p className="mt-4 text-[#4A5568] max-w-2xl">
                Seven long-term commitments — each designed with communities, delivered
                in partnership, and measured by real impact on Indian lives.
              </p>
            </div>
            <Link
              to="/initiatives"
              data-testid="all-initiatives-link"
              className="text-sm font-medium text-[#0A2540] underline underline-offset-4 hover:text-[#D4AF37]"
            >
              View all details →
            </Link>
          </div>

          <div className="divide-y divide-[#0A2540]/10 border-y border-[#0A2540]/10">
            {INITIATIVES.map((it, idx) => {
              const Icon = it.icon;
              return (
                <Link
                  key={it.slug}
                  to={`/initiatives/${it.slug}`}
                  data-testid={`initiative-card-${it.slug}`}
                  className="group grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8 items-center hover:bg-[#F9F8F6] transition-colors"
                >
                  {/* Number */}
                  <div className="col-span-2 md:col-span-1 font-serif-display text-[#0A2540]/40 text-xl md:text-2xl">
                    0{idx + 1}
                  </div>
                  {/* Colored icon */}
                  <div className="col-span-2 md:col-span-1 flex justify-center">
                    <div
                      className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: it.accent }}
                    >
                      <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={1.75} />
                    </div>
                  </div>
                  {/* Title + description */}
                  <div className="col-span-8 md:col-span-7">
                    <h3 className="font-serif-display text-lg md:text-2xl text-[#0A2540] leading-tight group-hover:text-[#D4AF37] transition-colors">
                      {it.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#4A5568] max-w-xl">{it.short}</p>
                  </div>
                  {/* Image */}
                  <div className="hidden md:block md:col-span-3">
                    <div className="aspect-[4/3] overflow-hidden rounded-xl border border-[#0A2540]/10 bg-[#F9F8F6]">
                      <img
                        src={it.image}
                        alt={it.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== IMPACT COUNTER ========== */}
      <ImpactCounter />

      {/* ========== WHY SUPPORT + 80G ========== */}
      <section data-testid="why-support-section" className="section-py">
        <div className="mx-auto max-w-7xl container-x grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">Why support us?</div>
            <h2 className="mt-3 font-serif-display text-3xl md:text-5xl text-[#0A2540]">
              Your rupee, working transparently.
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "Income tax benefit under Section 80G of the Income Tax Act, 1961.",
                "Official receipt and 80G certificate delivered by email immediately.",
                "Your support creates lasting social impact across 7 initiatives.",
                "Join a trusted, registered movement for a better India.",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-[#1A1A1A]">
                  <span className="mt-1 w-2 h-2 rounded-full bg-[#D4AF37] flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link
                to="/donate"
                data-testid="why-support-donate-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] text-white px-8 py-3.5 font-medium hover:bg-[#D4AF37] hover:text-[#0A2540] transition-colors"
              >
                Donate & Become a UB Mitra <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="rounded-3xl bg-[#0A2540] text-white p-8 md:p-10">
            <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.24em]">
              <ShieldCheck className="w-4 h-4" /> Tax Benefit — Section 80G
            </div>
            <h3 className="mt-4 font-serif-display text-3xl">
              Registered under 12AB. Approved under 80G.
            </h3>
            <p className="mt-3 text-white/80 text-sm">
              Donations to UB Maitreya Foundation are eligible for tax deduction under
              Section 80G of the Income Tax Act, 1961.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-white/50 uppercase text-xs tracking-[0.16em]">80G Approval</div>
                <div className="mt-1 font-medium">{FOUNDATION_INFO.reg80g}</div>
              </div>
              <div>
                <div className="text-white/50 uppercase text-xs tracking-[0.16em]">Approval Date</div>
                <div className="mt-1 font-medium">{FOUNDATION_INFO.reg80g_date}</div>
              </div>
              <div>
                <div className="text-white/50 uppercase text-xs tracking-[0.16em]">Valid For</div>
                <div className="mt-1 font-medium">{FOUNDATION_INFO.ay_validity}</div>
              </div>
              <div>
                <div className="text-white/50 uppercase text-xs tracking-[0.16em]">PAN / CIN</div>
                <div className="mt-1 font-medium">{FOUNDATION_INFO.pan}</div>
              </div>
              <div className="col-span-2 border-t border-white/10 pt-4 mt-2">
                <div className="text-white/50 uppercase text-xs tracking-[0.16em]">Bank</div>
                <div className="mt-1 font-medium">
                  {FOUNDATION_INFO.bank} · A/c {FOUNDATION_INFO.account}
                </div>
                <div className="text-white/70 text-xs mt-1">
                  IFSC {FOUNDATION_INFO.ifsc} · Branch {FOUNDATION_INFO.branch}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOUNDERS ========== */}
      <section data-testid="directors-section" className="section-py bg-white border-y border-[#0A2540]/10">
        <div className="mx-auto max-w-7xl container-x">
          <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">
            With gratitude
          </div>
          <h2 className="mt-3 font-serif-display text-3xl md:text-5xl text-[#0A2540] max-w-3xl">
            Led by hearts that believe India can rise, together.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-8 md:gap-14">
            {DIRECTORS.map((d) => (
              <div
                key={d.name}
                data-testid={`director-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="grid grid-cols-3 gap-6 items-start"
              >
                <div className="col-span-1 aspect-[3/4] rounded-2xl overflow-hidden border border-[#0A2540]/10 bg-[#F9F8F6]">
                  <img src={d.photo} alt={d.name} className="w-full h-full object-cover" />
                </div>
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">{d.role}</div>
                  <h3 className="mt-1 font-serif-display text-2xl text-[#0A2540]">{d.name}</h3>
                  <p className="mt-3 text-[#4A5568] text-sm leading-relaxed">{d.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== EVENTS & RECOGNITION ========== */}
      <section data-testid="events-section" className="section-py">
        <div className="mx-auto max-w-7xl container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">
                Events & Recognition
              </div>
              <h2 className="mt-3 font-serif-display text-3xl md:text-5xl text-[#0A2540] max-w-3xl">
                Felicitating Women Power in Governance.
              </h2>
              <p className="mt-4 text-[#4A5568] max-w-2xl">
                UB Maitreya Foundation honours women leaders shaping India from the
                grassroots up — Corporators of the Chhatrapati Sambhajinagar Municipal
                Corporation.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {EVENTS.map((ev) => (
              <article
                key={ev.slug}
                data-testid={`event-card-${ev.slug}`}
                className="group rounded-3xl overflow-hidden border border-[#0A2540]/10 bg-white hover:shadow-lg transition-shadow"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#0A2540]">
                  <img
                    src={ev.image}
                    alt={ev.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
                    {ev.theme}
                  </div>
                  <h3 className="mt-2 font-serif-display text-xl text-[#0A2540] leading-tight">
                    {ev.title}
                  </h3>
                  <div className="mt-1 text-sm text-[#4A5568]">{ev.subtitle}</div>
                  <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">
                    {ev.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="section-py">
        <div className="mx-auto max-w-5xl container-x text-center">
          <h2 className="font-serif-display text-3xl md:text-5xl text-[#0A2540]">
            Together, let's build a better India for all.
          </h2>
          <p className="mt-4 text-[#4A5568] max-w-2xl mx-auto">
            Every contribution — big or small — moves one of our seven pillars forward.
            Every donor gets an official 80G receipt in their inbox, instantly.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/donate"
              data-testid="cta-donate-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] text-white px-8 py-3.5 font-medium hover:bg-[#D4AF37] hover:text-[#0A2540] transition-colors"
            >
              Donate Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/csr"
              data-testid="cta-csr-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#0A2540] text-[#0A2540] px-8 py-3.5 font-medium hover:bg-[#0A2540] hover:text-white transition-colors"
            >
              Corporate CSR Enquiry
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
